import Control from "../../1. Core/Control";
import NP from "../../1. Core/NP";
import TopAppBar from "../../1. Core/Controls/TopAppBar/TopAppBar";
import CheckBox from "../../1. Core/Controls/CheckBox/CheckBox";
import NumField from "../../1. Core/Controls/TextField/NumField";
import Button from "../../1. Core/Controls/Button/Button";
import TextField from "../../1. Core/Controls/TextField/TextField";
import DataTable from "../../1. Core/Controls/DataTable/DataTable";
import {ServerAPI} from "../../1. Core/ServerAPI";
import "./NoviceBotsSettingsPanel.scss";

interface NoviceBotLogEntry {
    timestamp: string;
    level: string;
    userId?: string;
    fantasyId?: string;
    event: string;
    outcome: string;
    reasonCode?: string;
    counts?: string;
    detail?: string;
}

export default class NoviceBotsSettingsPanel extends Control {
    private _topAppBar: TopAppBar;
    private _contentNode: HTMLElement;

    constructor(settings?: object) {
        super(settings);
        this._render();
    }

    _render() {
        this.np_render();

        this._topAppBar = new TopAppBar({
            Caption: "Novice Bots",
            ParentNode: this._root,
            Class: "top-app-bar--no-shadow top-app-bar--light library-box-list__app-bar"
        });

        this._contentNode = NP.createNode(this._root, "novice-bots-settings-content");
    }

    getTopAppBar() {
        return this._topAppBar;
    }

    async refresh() {
        const settings = await ServerAPI.get<any>("/users/novice-bots-settings");
        this._contentNode.innerHTML = "";
        this._contentNode.style.paddingLeft = "24px";
        this._contentNode.style.paddingTop = "16px";
        this._contentNode.style.boxSizing = "border-box";

        const enabled = new CheckBox({
            ParentNode: this._contentNode,
            Class: "novice-bots-enabled",
            Content: "Enabled",
            Checked: !!settings.enabled
        });

        const gamesThreshold = new NumField({
            ParentNode: this._contentNode,
            FractionCount: 0,
            Min: 1,
            Max: 1000,
            Value: settings.gamesThreshold || 10,
            FloatingLabel: "Games threshold"
        });

        const botCount = new NumField({
            ParentNode: this._contentNode,
            FractionCount: 0,
            Min: 1,
            Max: 100,
            Value: settings.botCount || 19,
            FloatingLabel: "Bot count"
        });

        const maxAthletesPerBot = new NumField({
            ParentNode: this._contentNode,
            FractionCount: 0,
            Min: 1,
            Max: 10,
            Value: settings.maxAthletesPerBot || 2,
            FloatingLabel: "Max athletes per bot"
        });

        const maxAthletePoints = new NumField({
            ParentNode: this._contentNode,
            FractionCount: 0,
            Min: 1,
            Max: 1000,
            Value: settings.maxAthletePoints || 20,
            FloatingLabel: "Max athlete points"
        });

        const applyFieldLayout = (control: any) => {
            const root = control.getRoot ? control.getRoot() : null;
            if (!root)
                return;
            root.style.display = "block";
            root.style.width = "25vw";
            root.style.minWidth = "280px";
            root.style.maxWidth = "420px";
            root.style.marginBottom = "12px";
        };

        applyFieldLayout(gamesThreshold);
        applyFieldLayout(botCount);
        applyFieldLayout(maxAthletesPerBot);
        applyFieldLayout(maxAthletePoints);

        const enabledRoot = enabled.getRoot();
        enabledRoot.style.display = "block";
        enabledRoot.style.marginBottom = "12px";

        const saveButton = new Button({
            ParentNode: this._contentNode,
            Content: "Save",
            Class: "novice-bots-save-btn",
            EvClick: async () => {
                await ServerAPI.post("/users/novice-bots-settings", {
                    enabled: enabled.getChecked(),
                    gamesThreshold: gamesThreshold.getValue(),
                    botCount: botCount.getValue(),
                    maxAthletesPerBot: maxAthletesPerBot.getValue(),
                    maxAthletePoints: maxAthletePoints.getValue()
                });
                NP.showMessage({Content: "Novice bots settings saved"});
            }
        });
        const saveRoot = saveButton.getRoot();
        saveRoot.style.display = "inline-block";
        saveRoot.style.marginTop = "8px";
        saveRoot.style.marginLeft = "0";

        const eventsHeader = NP.createNode(this._contentNode);
        eventsHeader.textContent = "Diagnostics logs";
        eventsHeader.style.fontSize = "18px";
        eventsHeader.style.fontWeight = "bold";
        eventsHeader.style.marginTop = "32px";
        eventsHeader.style.marginBottom = "12px";

        const filters = NP.createNode(this._contentNode);
        filters.style.display = "flex";
        filters.style.gap = "12px";
        filters.style.alignItems = "flex-end";
        filters.style.flexWrap = "wrap";
        filters.style.marginBottom = "12px";

        const eventUserId = new NumField({
            ParentNode: filters, FractionCount: 0, Min: 0, Max: 2147483647,
            FloatingLabel: "User ID"
        });
        const eventFantasyId = new NumField({
            ParentNode: filters, FractionCount: 0, Min: 0, Max: 2147483647,
            FloatingLabel: "Fantasy ID"
        });
        const eventOutcome = new TextField({ParentNode: filters, FloatingLabel: "Outcome"});
        const eventReason = new TextField({ParentNode: filters, FloatingLabel: "Reason code"});
        const eventLimit = new NumField({
            ParentNode: filters, FractionCount: 0, Min: 1, Max: 500, Value: 100,
            FloatingLabel: "Limit"
        });
        [eventUserId, eventFantasyId, eventOutcome, eventReason, eventLimit].forEach((control: any) => {
            const root = control.getRoot();
            root.style.width = "160px";
        });

        const eventsTable = new DataTable({
            ParentNode: this._contentNode,
            ShowPaginator: false,
            Width: 1180,
            Columns: [
                {Content: "Time (UTC)", PropertyContent: "timestamp", Width: "145px"},
                {Content: "Level", PropertyContent: "level", Width: "60px"},
                {Content: "User", PropertyContent: "userId", Width: "65px"},
                {Content: "Fantasy", PropertyContent: "fantasyId", Width: "70px"},
                {Content: "Event", PropertyContent: "event", Width: "150px"},
                {Content: "Outcome", PropertyContent: "outcome", Width: "80px"},
                {Content: "Reason", PropertyContent: "reasonCode", Width: "190px"},
                {Content: "Counts", PropertyContent: "counts", Width: "130px"},
                {Content: "Detail", PropertyContent: "detail", Width: "300px"},
            ],
        });

        const refreshEvents = async () => {
            const params = new URLSearchParams({
                limit: String(eventLimit.getValue() || 100),
            });
            const userId = Number(eventUserId.getValue());
            const fantasyId = Number(eventFantasyId.getValue());
            const outcome = (eventOutcome.getValue() || "").trim();
            const reasonCode = (eventReason.getValue() || "").trim();
            if (userId > 0) params.set("userId", String(userId));
            if (fantasyId > 0) params.set("fantasyId", String(fantasyId));
            if (outcome) params.set("outcome", outcome);
            if (reasonCode) params.set("reasonCode", reasonCode);
            const events = await ServerAPI.get<NoviceBotLogEntry[]>(`/users/novice-bot-events?${params.toString()}`);
            eventsTable.setRows((events || []).map((event) => ({
                ...event,
                userId: event.userId || "—",
                fantasyId: event.fantasyId || "—",
                reasonCode: event.reasonCode || "—",
                counts: event.counts || "—",
                detail: event.detail || "—",
            })));
        };

        new Button({
            ParentNode: filters,
            Content: "Refresh events",
            EvClick: refreshEvents,
        });
        await refreshEvents();
    }

    setWidth(value: number) {
        super.setWidth(value);
        if (this._contentNode)
            this._contentNode.style.width = `${value}px`;
    }

    setHeight(value: number) {
        super.setHeight(value);
        if (this._contentNode)
            this._contentNode.style.height = `${value - 64}px`;
    }
}

